import React from 'react';
import { Button } from '../shadcn/components/ui/button';
import LoadingButton from './Common/LoadingButton';
import StatusAlert from './Common/StatusAlert';
import LabeledTextField from './Common/LabeledTextField';
import { useTranslation } from '../context/Internationalization';
import EmailCodeInput from '../pages/Login/EmailCodeInput';
import useEmailChangeCode from './useEmailChangeCode';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../shadcn/components/ui/dialog";

const SmsLoginEmailPromptModal = ({ open = false, onClose = () => {}, user, language }) => {
  const { t } = useTranslation();
  const flow = useEmailChangeCode({ open, userId: user?._id, language, onClose });
  const title = user?.email ? t('change_email') : t('add_email_title');
  const close = () => { if (!flow.busy && !flow.verified) onClose(); };
  const remindLater = () => {
    localStorage.setItem('emailPromptRemindAfter', String(Date.now() + 24 * 60 * 60 * 1000));
    close();
  };
  return (
    <Dialog
      open={Boolean(open)}
      onOpenChange={(next, details) => !next && close(details?.event)}
    >
      <DialogContent closeLabel={t("close")}>
      <form className="contents" onSubmit={flow.challenge ? flow.verify : flow.send}>
        <DialogHeader>
          <DialogTitle>{flow.challenge ? t('email_change_verify_title') : title}</DialogTitle>
        </DialogHeader>
        <div className="flex min-w-0 flex-col gap-4">
          <div className={`flex w-full flex-col gap-4 ${flow.challenge ? 'pt-2' : ''}`}>
            {flow.error && <StatusAlert severity="error">{t(flow.verified ? 'email_change_sync_retry_notice' : flow.error)}</StatusAlert>}
            {flow.blocked && <StatusAlert severity="warning">{t('email_change_impersonation_forbidden')}</StatusAlert>}
            {flow.challenge ? <>
              <p id="email-change-code-help" className="text-sm [overflow-wrap:anywhere]">
                {t('email_code_sent_to')} <strong>{flow.challenge.email}</strong>. {!flow.verified && t('email_code_expiry_help')}
              </p>
              <div className="flex justify-center">
                <EmailCodeInput value={flow.code} onChange={flow.setCode} disabled={flow.busy || flow.blocked || flow.expired || flow.verified}
                  label={t('email_code_label')} error={Boolean(flow.error)} descriptionId="email-change-code-help" />
              </div>
              {flow.expired && <StatusAlert severity="warning">{t('email_code_expired')}</StatusAlert>}
              <div className="flex flex-wrap justify-between gap-2">
                <Button type="button" variant="ghost" onClick={flow.send} disabled={flow.busy || flow.blocked || flow.resendIn > 0 || flow.verified}>
                  {flow.resendIn ? `${t('email_code_resend_in')} ${flow.resendIn}s` : t('email_code_resend')}
                </Button>
                <Button type="button" variant="ghost" onClick={flow.edit} disabled={flow.busy || flow.verified}>{t('email_change_edit_address')}</Button>
              </div>
            </> : <>
              {user?.email && <p className="text-sm">{t('current_email')}: {user.email}</p>}
              <p className="text-sm">{t('email_change_code_notice')}</p>
              <LabeledTextField autoFocus required type="email" label={user?.email ? t('new_email') : t('email')}
                value={flow.email} disabled={flow.busy || flow.blocked} onChange={event => flow.setEmail(event.target.value)} />
              {flow.resendIn > 0 && <p className="text-sm">
                {t('email_code_resend_in')} {flow.resendIn}s
              </p>}
            </>}
          </div>
        </div>
        <DialogFooter>
          {!user?.email && !flow.challenge && <Button type="button" variant="outline" onClick={remindLater} disabled={flow.busy}>
            {t('remind_me_later')}
          </Button>}
          <Button type="button" variant="outline" onClick={close} disabled={flow.busy || flow.verified}>{t('cancel')}</Button>
          <LoadingButton type="submit" loading={flow.busy}
            disabled={flow.blocked || (flow.challenge ? !flow.verified && (flow.code.length !== 6 || flow.expired) : flow.resendIn > 0)}>
            {flow.verified ? t('retry_synchronization') : flow.challenge ? t('email_change_verify_save') : t('email_change_send_code')}
          </LoadingButton>
        </DialogFooter>
      </form>
      </DialogContent>
    </Dialog>
  );
};
export default SmsLoginEmailPromptModal;
